import Service from '@ember/service';
import { tracked } from 'tracked-built-ins';
import type { Promisable } from 'type-fest';
import { LocalStorageAdapter } from './favorite-adapters/local-storage';

export interface FavoriteAdapter {
  add(favorite: string): Promisable<void>;
  getAll(): Promisable<string[]>;
  set(...favorites: string[]): Promisable<void>;
  remove(favorite: string): Promisable<void>;
  isFavorite(id: string): Promisable<boolean>;
}

export default class FavoriteService extends Service {
  private adapter: FavoriteAdapter = new LocalStorageAdapter();

  @tracked
  // acts as a local "cache". We do not want to make a request each time that the favoriteIds are needed
  public favoriteIds: string[] = [];

  async switchAdapter(adapter: FavoriteAdapter) {
    this.adapter = adapter;
    await this.loadFavorites();
  }

  async add(productId: string) {
    await this.adapter.add(productId);
    this.favoriteIds.push(productId);
    // remove duplicates just in case
    this.favoriteIds = [...new Set(this.favoriteIds)];
  }

  async loadFavorites() {
    const favorites = await this.adapter.getAll();
    this.favoriteIds = [...favorites];
  }

  isLocalAdapter() {
    return this.adapter instanceof LocalStorageAdapter;
  }

  async getFavorites() {
    return this.favoriteIds;
  }

  async clear() {
    await this.adapter.set();
    this.favoriteIds = [];
  }

  async remove(productId: string) {
    await this.adapter.remove(productId);
    this.favoriteIds = this.favoriteIds.filter(
      (favoriteId) => favoriteId !== productId
    );
  }

  isFavorite(productId: string) {
    return this.favoriteIds.includes(productId);
  }
}

declare module '@ember/service' {
  // eslint-disable-next-line no-unused-vars
  interface Registry {
    favorite: FavoriteService;
  }
}
